import { themes } from '../themeList'
import { categoriseComplaints } from './complaintCategories'

// The sections a Post Event Report can include, in document order, and the
// parts each is built from. In the generator every part shows a one-line
// summary of its figure so a planner can see what will be printed and leave
// out a single weak number without dropping the whole section.
//
// unavailable(data, pea) -> reason string when there is nothing to show
// summary(data, pea)     -> the figure shown beside the part's checkbox
// flag(data, pea)        -> a short note when the figure may read badly
// hideWhenUnavailable     -> leave the part out of the picker rather than grey it out

const writtenThemes = (pea) =>
  themes.filter(
    ({ dbName }) => pea?.[dbName]?.analysis?.trim() || pea?.[dbName]?.recommendations?.trim(),
  )

const pct = (v) => (v === null || v === undefined ? '-' : `${v}%`)
const mins = (v) => (v === null || v === undefined ? '-' : `${v} min`)
const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`
const top = (counts) => {
  const entries = Object.entries(counts || {}).sort((a, b) => b[1] - a[1])
  return entries.length ? `${entries[0][0]} (${entries[0][1]})` : 'None recorded'
}
const hour = (h) => (h === null || h === undefined ? '-' : `${String(h).padStart(2, '0')}:00`)

const noRoom = (data) => (data.controlRoom ? null : 'No control room is linked to this event')
const noPatients = (data) =>
  data.overall.clinical.total ? null : 'No patient records are linked to this event'

export const REPORT_SECTIONS = [
  {
    key: 'executive',
    group: 'Overview',
    label: 'Executive summary',
    description: 'Headline figures, key findings, forecast against actual and the debrief summary.',
    defaultOn: true,
    unavailable: () => null,
    parts: [
      { key: 'headline', label: 'Headline figures', summary: () => 'Tiles for the figures included elsewhere' },
      { key: 'findings', label: 'Key findings', summary: () => 'Written from the included figures' },
      {
        key: 'forecast',
        label: 'Forecast against actual',
        unavailable: (data) => {
          const dp = data.event.demandPrediction || {}
          return dp.expectedPresentationsPlanning ?? dp.expectedPresentationsMin ?? dp.hospitalTransfersPlanning ?? dp.hospitalTransfersMin ?? dp.peakHourStart
            ? null
            : 'No forecast in the medical plan'
        },
        summary: (data) => {
          const dp = data.event.demandPrediction || {}
          const c = data.overall.clinical
          return [
            dp.expectedPresentationsPlanning ?? dp.expectedPresentationsMax
              ? `${c.total} presentations vs ${dp.expectedPresentationsPlanning ?? dp.expectedPresentationsMax} forecast`
              : null,
            dp.hospitalTransfersPlanning ?? dp.hospitalTransfersMax
              ? `${c.conveyed} to hospital vs ${dp.hospitalTransfersPlanning ?? dp.hospitalTransfersMax}`
              : null,
          ]
            .filter(Boolean)
            .join('; ')
        },
        flag: (data) => {
          const dp = data.event.demandPrediction || {}
          const c = data.overall.clinical
          return dp.hospitalTransfersMax !== undefined && dp.hospitalTransfersMax !== null && c.conveyed > dp.hospitalTransfersMax
            ? 'Hospital transfers were above the forecast'
            : null
        },
      },
      {
        key: 'debrief',
        label: 'Debrief summary',
        unavailable: (data, pea) => (pea?.summary?.trim() ? null : 'No summary written on the Post-event tab'),
        summary: () => 'From the PEA summary',
      },
    ],
  },
  {
    key: 'overview',
    group: 'Overview',
    label: 'Event and resourcing',
    description: 'Event details, the staff and vehicles we deployed, and the medical plan recommendation.',
    defaultOn: true,
    unavailable: () => null,
    parts: [
      { key: 'details', label: 'Event details', summary: (data) => data.event.client || 'Venue, dates, attendance' },
      { key: 'staff', label: 'Staff deployed', summary: (data) => plural(data.staffing.overall.people, 'person', 'people') },
      {
        key: 'shiftFill',
        label: 'Shifts filled',
        unavailable: (data) => (data.staffing.overall.shifts ? null : 'No shifts on this event'),
        summary: (data) => {
          const s = data.staffing.overall
          return `${s.filled} of ${s.shifts} (${pct(s.fillRate)})`
        },
        flag: (data) =>
          data.staffing.overall.unfilled ? `${plural(data.staffing.overall.unfilled, 'shift')} left unfilled` : null,
      },
      {
        key: 'hours',
        label: 'Planned and clocked hours',
        summary: (data) => {
          const s = data.staffing.overall
          return `${s.plannedHours} planned / ${s.clockedShifts ? `${s.workedHours} clocked` : 'not clocked'}`
        },
      },
      {
        key: 'punctuality',
        label: 'Punctuality',
        unavailable: (data) => (data.staffing.overall.onTimeRate === null ? 'No clock-in times recorded' : null),
        summary: (data) => `${pct(data.staffing.overall.onTimeRate)} on time`,
        flag: (data) =>
          data.staffing.overall.onTimeRate !== null && data.staffing.overall.onTimeRate < 90
            ? 'Fewer than 90% of shifts started on time'
            : null,
      },
      { key: 'roles', label: 'Staff by role', summary: (data) => top(data.staffing.overall.roles) },
      {
        key: 'recommended',
        label: 'Medical plan recommendation',
        unavailable: (data) =>
          Object.values(data.event.resourceRecommendation || {}).some(Boolean) ? null : 'No recommendation in the medical plan',
        summary: (data) => {
          const rr = data.event.resourceRecommendation || {}
          return rr.clinicalStaff ? `${rr.clinicalStaff} clinical staff recommended` : 'Recommended resources'
        },
      },
      {
        key: 'vehicles',
        label: 'Vehicles deployed',
        summary: (data) => plural(data.staffing.vehicles.length, 'vehicle'),
      },
    ],
  },
  {
    key: 'clinical',
    group: 'Clinical',
    label: 'Clinical activity',
    description: 'Presentations by hour, presenting complaint, outcome, age, sex and record type.',
    defaultOn: true,
    unavailable: noPatients,
    parts: [
      {
        key: 'headline',
        label: 'Headline figures',
        summary: (data) => {
          const c = data.overall.clinical
          return `${c.total} presentations, ${c.occurred?.Yes || 0} occurred at the event`
        },
      },
      { key: 'hourly', label: 'Presentations by hour', summary: (data) => `Busiest ${hour(data.overall.clinical.peakHour?.hour)}` },
      {
        key: 'complaints',
        label: 'Presenting complaints',
        summary: (data) => `Top category: ${top(categoriseComplaints(data.overall.clinical.complaints))}`,
      },
      { key: 'outcomes', label: 'Outcomes', summary: (data) => top(data.overall.clinical.outcomes) },
      { key: 'age', label: 'Age', summary: (data) => top(data.overall.clinical.ageBands) },
      { key: 'sex', label: 'Sex', summary: (data) => top(data.overall.clinical.sex) },
      { key: 'recordType', label: 'Record type', summary: (data) => top(data.overall.clinical.byMode) },
      {
        key: 'welfare',
        label: 'Welfare support',
        unavailable: (data) =>
          Object.keys(data.overall.clinical.welfareCategories || {}).length ? null : 'No welfare categories recorded',
        summary: (data) => top(data.overall.clinical.welfareCategories),
      },
    ],
  },
  {
    key: 'days',
    group: 'Clinical',
    label: 'Day by day',
    description: 'Operational days (05:00 to 05:00) compared, then a page for each day.',
    defaultOn: true,
    unavailable: (data) => (data.days.length > 1 ? null : 'The event runs on a single operational day'),
    parts: [
      {
        key: 'comparison',
        label: 'Comparison of days',
        summary: (data) => {
          const busiest = [...data.days].sort((a, b) => b.clinical.total - a.clinical.total)[0]
          return busiest ? `Busiest ${busiest.key.slice(5).split('-').reverse().join('/')} (${busiest.clinical.total})` : '-'
        },
      },
      { key: 'pages', label: 'A page for each day', summary: (data) => plural(data.days.length, 'page') },
    ],
  },
  {
    key: 'controlRoom',
    group: 'Control room',
    label: 'Control room performance',
    description: 'Incident mix, priorities, response intervals and resource utilisation.',
    defaultOn: true,
    unavailable: noRoom,
    parts: [
      {
        key: 'headline',
        label: 'Headline figures',
        summary: (data) => {
          const s = data.controlRoom.summary
          return `${s.total} incidents, median response ${mins(s.intervals.callToScene.median)}`
        },
      },
      {
        key: 'intervals',
        label: 'Response intervals',
        summary: (data) => `90% on scene within ${mins(data.controlRoom.summary.intervals.callToScene.p90)}`,
      },
      {
        key: 'priority',
        label: 'Response by priority',
        summary: (data) => {
          const crit = data.controlRoom.summary.responseByPriority.Critical
          return crit ? `Critical median ${mins(crit.callToScene.median)}` : 'By priority'
        },
      },
      { key: 'hourly', label: 'Incidents by hour', summary: (data) => `Busiest ${hour(data.controlRoom.summary.peakHour?.hour)}` },
      { key: 'types', label: 'Incident type', summary: (data) => top(data.controlRoom.summary.byType) },
      {
        key: 'triage',
        label: 'Call triage category',
        unavailable: (data) =>
          Object.keys(data.controlRoom.summary.triageCategories || {}).length ? null : 'Calls were not triaged',
        summary: (data) => top(data.controlRoom.summary.triageCategories),
      },
      { key: 'resources', label: 'Resources and hours', summary: (data) => plural(data.controlRoom.resources.length, 'call sign') },
      { key: 'activity', label: 'Control room activity', summary: (data) => `${data.controlRoom.logEntries} log entries` },
    ],
  },
  {
    key: 'treatment',
    group: 'Control room',
    label: 'Treatment locations',
    description: 'Patients per location, length of stay and peak occupancy.',
    defaultOn: true,
    unavailable: (data) => {
      if (!data.controlRoom) return 'No control room is linked to this event'
      return data.treatment?.overall.patients ? null : 'No treatment location stays were recorded'
    },
    parts: [
      {
        key: 'headline',
        label: 'Headline figures',
        summary: (data) => `${data.treatment.overall.patients} patients, median stay ${mins(data.treatment.overall.lengthOfStay.median)}`,
      },
      { key: 'table', label: 'By location', summary: (data) => plural(data.treatment.locations.length, 'location') },
      {
        key: 'occupancy',
        label: 'Occupancy charts',
        summary: (data) => `Peak ${data.treatment.overall.peak.count} at once`,
      },
    ],
  },
  {
    key: 'decisions',
    group: 'Control room',
    label: 'Significant decisions',
    description: 'Decision log entries made in the control room (problem and decision).',
    defaultOn: false,
    unavailable: (data) => {
      if (!data.controlRoom) return 'No control room is linked to this event'
      return data.controlRoom.decisions.length ? null : 'No decisions were logged'
    },
    parts: [
      { key: 'log', label: 'Decision log', summary: (data) => plural(data.controlRoom.decisions.length, 'decision') },
    ],
  },
  {
    key: 'nhsImpact',
    group: 'Clinical',
    label: 'NHS impact',
    description: 'Conveyances, receiving hospitals, NHS ambulance handovers and non-conveyance.',
    defaultOn: true,
    unavailable: noPatients,
    parts: [
      {
        key: 'headline',
        label: 'Headline figures',
        summary: (data) => `${data.overall.clinical.conveyed} to hospital (${pct(data.overall.clinical.conveyanceRate)})`,
      },
      { key: 'hospitals', label: 'Receiving hospitals', summary: (data) => top(data.overall.clinical.destinations) },
      { key: 'destinationTypes', label: 'Type of destination', summary: (data) => top(data.overall.clinical.destinationTypes) },
    ],
  },
  {
    key: 'medicines',
    group: 'Clinical',
    label: 'Medicines administered',
    description: 'Administrations recorded on event records, by medicine.',
    defaultOn: true,
    unavailable: (data) =>
      data.overall.medicines.administrations ? null : 'No medicines were recorded on event records',
    parts: [
      {
        key: 'table',
        label: 'By medicine',
        summary: (data) => {
          const m = data.overall.medicines
          return `${m.administrations} administrations${m.controlled ? `, ${m.controlled} controlled` : ''}`
        },
      },
    ],
  },
  {
    key: 'healthSafety',
    group: 'Clinical',
    label: 'Health and safety concerns',
    description: 'Concerns raised by crews for the organiser, with the action taken.',
    defaultOn: true,
    unavailable: () => null,
    parts: [
      {
        key: 'concerns',
        label: 'Concerns raised',
        summary: (data) => (data.healthSafety.length ? plural(data.healthSafety.length, 'concern') : 'None raised'),
        flag: (data) => {
          const missed = data.healthSafety.filter((h) => !h.organiserNotified).length
          return missed ? `${missed} not marked as passed to the organiser` : null
        },
      },
    ],
  },
  {
    key: 'patientFeedback',
    group: 'Feedback',
    label: 'Patient compliments and complaints',
    description: 'Feedback from patients whose record is linked to this event.',
    defaultOn: true,
    unavailable: (data) =>
      data.feedback.patients.responses ? null : 'No patient feedback is linked to this event',
    parts: [
      {
        key: 'counts',
        label: 'Compliments and complaints',
        summary: (data) => {
          const p = data.feedback.patients
          return `${plural(p.compliments, 'compliment')}, ${plural(p.complaints, 'complaint')}`
        },
        flag: (data) => (data.feedback.patients.complaints ? 'Includes complaints' : null),
      },
      {
        key: 'ratings',
        label: 'How patients rated their care',
        unavailable: (data) =>
          Object.values(data.feedback.patients.measures).some(Boolean) ? null : 'No ratings given',
        summary: (data) => {
          const d = data.feedback.patients.measures.dignityRespect
          return d ? `${pct(d.rate)} dignity and respect` : 'Care ratings'
        },
      },
      {
        key: 'recommend',
        label: 'Would recommend',
        unavailable: (data) => (data.feedback.patients.recommendRate === null ? 'Not answered' : null),
        summary: (data) => pct(data.feedback.patients.recommendRate),
        flag: (data) =>
          data.feedback.patients.recommendRate !== null && data.feedback.patients.recommendRate < 80
            ? 'Under 80% would recommend'
            : null,
      },
      {
        key: 'comments',
        label: 'Quoted comments',
        unavailable: (data) => (data.feedback.patients.comments.length ? null : 'No comments patients agreed to share'),
        summary: (data) => plural(data.feedback.patients.comments.length, 'comment'),
      },
    ],
  },
  {
    key: 'organiserFeedback',
    group: 'Feedback',
    label: 'Event organiser feedback',
    description: 'Ratings and comments from the organiser feedback link, plus PEA organiser notes.',
    defaultOn: true,
    unavailable: (data, pea) =>
      data.feedback.organiser.responses || pea?.clientFeedback?.trim()
        ? null
        : 'No organiser feedback has been received',
    parts: [
      {
        key: 'ratings',
        label: 'Ratings',
        unavailable: (data) => (data.feedback.organiser.responses ? null : 'No feedback-link responses'),
        summary: (data) => `${data.feedback.organiser.overall ?? '-'} / 5 overall`,
        flag: (data) =>
          data.feedback.organiser.overall !== null && data.feedback.organiser.overall < 4 ? 'Overall rating under 4' : null,
      },
      {
        key: 'bookAgain',
        label: 'Would book again',
        unavailable: (data) => (data.feedback.organiser.responses ? null : 'No feedback-link responses'),
        summary: (data) =>
          Object.entries(data.feedback.organiser.bookAgain).map(([k, v]) => `${k} ${v}`).join(', ') || 'Not answered',
        flag: (data) => (data.feedback.organiser.bookAgain.No ? 'An organiser would not book again' : null),
      },
      {
        key: 'comments',
        label: 'Organiser comments',
        unavailable: (data) => (data.feedback.organiser.responses ? null : 'No feedback-link responses'),
        summary: (data) => plural(data.feedback.organiser.responses, 'response'),
      },
      {
        key: 'notes',
        label: 'Feedback notes from the Post-event tab',
        unavailable: (data, pea) => (pea?.clientFeedback?.trim() ? null : 'No notes written'),
        summary: () => 'Organiser feedback field',
      },
    ],
  },
  {
    key: 'staffFeedback',
    group: 'Feedback',
    label: 'Staff feedback',
    description: 'End-of-shift comments and the PEA staff feedback summary (no names).',
    defaultOn: false,
    unavailable: (data, pea) =>
      data.feedback.staff.responses || pea?.staffFeedback?.trim()
        ? null
        : 'No staff feedback has been recorded',
    parts: [
      {
        key: 'summary',
        label: 'Staff feedback summary',
        unavailable: (data, pea) => (pea?.staffFeedback?.trim() ? null : 'No summary written'),
        summary: () => 'From the Post-event tab',
      },
      {
        key: 'comments',
        label: 'End-of-shift comments',
        unavailable: (data) => (data.feedback.staff.responses ? null : 'No end-of-shift comments'),
        summary: (data) => plural(data.feedback.staff.responses, 'comment'),
      },
    ],
  },
  {
    key: 'lessons',
    group: 'Debrief',
    label: 'Lessons and recommendations',
    description: 'The analysis and recommendations entered under each PEA theme.',
    defaultOn: true,
    unavailable: (data, pea) => (writtenThemes(pea).length ? null : 'No themes have been written up'),
    // One part per theme, so a planner can keep a theme internal
    parts: themes.map(({ title, dbName }) => ({
      key: dbName,
      label: title,
      // Nine themes, usually only a few written up: don't list the blanks
      hideWhenUnavailable: true,
      unavailable: (data, pea) =>
        pea?.[dbName]?.analysis?.trim() || pea?.[dbName]?.recommendations?.trim() ? null : 'Not written up',
      summary: (data, pea) => (pea?.[dbName]?.recommendations?.trim() ? 'With recommendation' : 'Analysis only'),
    })),
  },
  {
    key: 'actions',
    group: 'Debrief',
    label: 'Action plan',
    description: 'PEA actions with owner, due date and completion.',
    defaultOn: true,
    unavailable: (data) => (data.actions.length ? null : 'No actions have been added'),
    parts: [
      {
        key: 'table',
        label: 'Actions',
        summary: (data) => `${data.actions.length} (${data.actions.filter((a) => a.completedAt).length} complete)`,
        flag: (data) => {
          const overdue = data.actions.filter((a) => !a.completedAt && a.when && new Date(a.when) < new Date()).length
          return overdue ? `${plural(overdue, 'action')} overdue` : null
        },
      },
    ],
  },
  {
    key: 'appendix',
    group: 'Debrief',
    label: 'Methodology and data quality',
    description: 'How figures are calculated, and how complete the underlying records are.',
    defaultOn: true,
    unavailable: () => null,
    parts: [
      { key: 'method', label: 'How the figures are worked out', summary: () => 'Definitions' },
      {
        key: 'quality',
        label: 'Completeness of records',
        summary: (data) => {
          const q = data.completeness
          return `${q.draftRecords} draft, ${q.noOutcome} without an outcome`
        },
        flag: (data) => (data.completeness.draftRecords ? 'Some records are still in draft' : null),
      },
    ],
  },
]

export const SECTION_GROUPS = [...new Set(REPORT_SECTIONS.map((s) => s.group))]

export const partKey = (section, part) => `${section}.${part}`

export const defaultSelection = () => ({
  sections: Object.fromEntries(REPORT_SECTIONS.map((s) => [s.key, s.defaultOn])),
  parts: Object.fromEntries(
    REPORT_SECTIONS.flatMap((s) => s.parts.map((p) => [partKey(s.key, p.key), true])),
  ),
  complaintView: 'category',
})

// Saved choices over the defaults, so parts added later default to on
export const mergeSelection = (saved) => {
  const base = defaultSelection()
  if (!saved || typeof saved !== 'object' || !saved.sections) return base
  return {
    sections: { ...base.sections, ...saved.sections },
    parts: { ...base.parts, ...(saved.parts || {}) },
    complaintView: saved.complaintView === 'individual' ? 'individual' : 'category',
  }
}

// Why each section / part can't be included right now: { [key]: reason|null }
export const availability = (data, pea) => {
  const out = {}
  REPORT_SECTIONS.forEach((s) => {
    out[s.key] = s.unavailable(data, pea)
    s.parts.forEach((p) => {
      // A part's own check can assume its section has data
      out[partKey(s.key, p.key)] = out[s.key] || (p.unavailable ? p.unavailable(data, pea) : null)
    })
  })
  return out
}

// What the PDF prints: a section is in when it is ticked, available and
// has at least one part in; include.part(section, part) gates each block.
export const buildInclude = (data, pea, selection) => {
  const reasons = availability(data, pea)
  const parts = {}
  const include = { complaintView: selection.complaintView || 'category' }
  REPORT_SECTIONS.forEach((s) => {
    const sectionOn = !!selection.sections[s.key] && !reasons[s.key]
    let any = false
    s.parts.forEach((p) => {
      const k = partKey(s.key, p.key)
      parts[k] = sectionOn && selection.parts[k] !== false && !reasons[k]
      if (parts[k]) any = true
    })
    include[s.key] = sectionOn && any
  })
  include.part = (section, part) => !!include[section] && !!parts[partKey(section, part)]
  // For figures repeated outside their own section (the day pages): a part
  // the planner unticked stays hidden there too, even if its section is off
  include.allowed = (section, part) => {
    const k = partKey(section, part)
    return selection.parts[k] !== false && !reasons[k] && !reasons[section]
  }
  return include
}
